/* eslint-disable immutable/no-mutation */

import { useToast } from '@hooks/useToast';
import { Errors, UseErrorsOptions } from './types';
import { useTranslation } from 'react-i18next';

export const useErrors = (options?: UseErrorsOptions) => {
  const { t, i18n } = useTranslation('app');
  const { toast } = useToast();

  const translate = (key: string) => {
    const [code, ...rest] = key.split(' ');
    if (rest.length && i18n.exists(`errors.${code}`, { ns: 'app' }))
      return t(`errors.${code}`, { details: rest.join(' ') });
    return t([`errors.${key}`, key]);
  };

  const collectErrors = (data: Errors) => {
    let toastErrors: string[] = [];
    const formErrors: Record<string, string> = {};

    if (typeof data === 'string') {
      toastErrors.push(translate(data));
    } else if (Array.isArray(data)) {
      toastErrors = [...toastErrors, ...data.map((el) => translate(el))];
    } else {
      const { base, ...rest } = data;
      if (Array.isArray(base)) toastErrors = [...toastErrors, ...base.map((el) => translate(el))];
      else if (typeof base === 'string') toastErrors.push(translate(base));
      const keys = Object.keys(rest);
      keys.forEach((key) => {
        const value = rest[key];
        if (Array.isArray(value)) formErrors[key] = value.map((el) => translate(el)).join('\n');
        else formErrors[key] = translate(value);
      });
    }

    return { toastErrors, formErrors };
  };

  return (data?: Errors, setFormError?: (key: string, value: string) => void) => {
    if (!data) return;

    const { toastErrors, formErrors } = collectErrors(data);

    if (options) {
      const { allInToast } = options;
      if (allInToast) {
        Object.keys(formErrors).forEach((key) => {
          toastErrors.push(`${key}\n${formErrors[key]}`);
        });
      }
    }

    if (setFormError) {
      Object.keys(formErrors).forEach((key) => {
        setFormError(key, formErrors[key]);
      });
    }

    toastErrors.forEach((description) => {
      toast({
        title: t('error'),
        description,
        variant: 'destructive',
      });
    });
  };
};
